import ReactDOM from "react-dom/client";
import { setContext } from "@apollo/client/link/context";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import * as Sentry from "@sentry/browser";
import "./index.css";
import "react-toastify/dist/ReactToastify.min.css";
import App from "./App";
import { API_PRODUCTION, AUTH_TOKEN, SENTRY_DSN } from "./constants";
import { analytics } from "./utils/analytics";
import { demoLink } from "./features/demo/demo-link";
import { setDemoClient } from "./features/demo/demo-session";
import { registerServiceWorker } from "./features/pwa/register-service-worker";
// Side-effect import, and it must stay above `root.render`. Chrome fires
// `beforeinstallprompt` before React mounts and the event never replays, so
// the listener has to exist before the app does.
import "./features/pwa/install-prompt";
// Self-hosted rather than the Google Fonts CDN, which transmits the visitor's
// IP to a third party — avoided deliberately for an EU-facing finance app.
//
// Variable faces, so one file per family covers every weight. Subsetting by
// `unicode-range` means the `latin-ext` payload (Croatian č ć đ š ž, Slovenian,
// Hungarian ő ű) only downloads when those characters actually render.
import "@fontsource-variable/source-sans-3";
import "@fontsource-variable/literata";

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [
    // functional integrations replace the old class-based ones
    Sentry.browserTracingIntegration(), // ← no more `new Sentry.BrowserTracing()`
    Sentry.replayIntegration(), // ← no more `new Sentry.Replay()`
  ],

  // distributed tracing targets now live at the top level
  tracePropagationTargets: [/^https:\/\/yourmonthly\.app/],

  // Performance Monitoring
  tracesSampleRate: 0.5,

  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Finance app: don't backfill PII (IP addresses, etc.) into Sentry
  sendDefaultPii: false,
});

const apiUrl =
  import.meta.env.MODE === "production"
    ? API_PRODUCTION
    : "http://localhost:3001";

const httpLink = createHttpLink({
  uri: apiUrl,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const client = new ApolloClient({
  // The demo link sits in front of everything: while a demo is running it
  // answers operations from the local dataset, so no component, page or route
  // needs to know a demo exists. See `features/demo/demo-link.ts`.
  link: ApolloLink.from([demoLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

// Entering and leaving the demo swaps every figure in the app, so the cache
// cannot be allowed to carry answers across that boundary.
setDemoClient(client);

// Initialize Mixpanel
analytics.init();

// Caches nothing and answers no request. It exists only so Chrome treats the
// app as installable — see `public/sw.js`.
registerServiceWorker();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <ApolloProvider client={client}>
    <App />
  </ApolloProvider>,
);

// Web vitals are not reported anywhere. `reportWebVitals` is still here if that
// changes; give it a reporter that sends somewhere rather than console.log.
